import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Pagination from '@/components/base/Pagination';

interface ErrorLog {
  id: number;
  error_type: string;
  error_message: string | null;
  error_detail: string | null;
  source: string | null;
  context: Record<string, unknown> | null;
  created_at: string;
}

const ERROR_TYPE_LABELS: Record<string, string> = {
  db_insert: 'DB 저장 오류',
  api_sync_insert: 'API 동기화 저장 오류',
  excel_parse: '엑셀 파싱 오류',
  api_fetch: 'API 호출 오류',
  validation: '데이터 검증 오류',
  unknown: '기타 오류',
};

const ERROR_TYPE_ICONS: Record<string, string> = {
  db_insert: 'ri-database-2-line',
  api_sync_insert: 'ri-cloud-line',
  excel_parse: 'ri-file-excel-2-line',
  api_fetch: 'ri-global-line',
  validation: 'ri-error-warning-line',
  unknown: 'ri-question-line',
};

const SOURCE_LABELS: Record<string, string> = {
  excel_import: '엑셀 업로드',
  api_sync_auto: 'API 자동동기화',
  api_sync_manual: 'API 수동동기화',
  draw_manager: '당첨번호 관리',
};

const PAGE_SIZE = 15;

function getToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getYesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleString('ko-KR');
}

function formatDetail(detail: string | null): string {
  if (!detail) return '-';
  try {
    const parsed = JSON.parse(detail);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return detail;
  }
}

export default function ErrorLogManager() {
  const [searchParams, setSearchParams] = useSearchParams();

  // 상태를 URL 쿼리 파라미터에서 초기화
  const initialFrom = searchParams.get('from') || getToday();
  const initialTo = searchParams.get('to') || getToday();
  const initialKeyword = searchParams.get('keyword') || '';
  const initialSource = searchParams.get('source') || '';

  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [sourceFilter, setSourceFilter] = useState(initialSource);
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [clearingAll, setClearingAll] = useState(false);
  const [clearingOld, setClearingOld] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('error_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fromDate) {
        query = query.gte('created_at', `${fromDate}T00:00:00`);
      }
      if (toDate) {
        query = query.lte('created_at', `${toDate}T23:59:59`);
      }
      if (sourceFilter) {
        query = query.eq('source', sourceFilter);
      }
      if (keyword.trim()) {
        query = query.or(
          `error_message.ilike.%${keyword.trim()}%,error_detail.ilike.%${keyword.trim()}%,error_type.ilike.%${keyword.trim()}%`
        );
      }

      const { data, count, error: fetchErr } = await query;

      if (fetchErr) throw fetchErr;
      if (data) {
        setLogs(data as ErrorLog[]);
      }
      if (count !== null) {
        setTotalCount(count);
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[ErrorLogManager] 로그 조회 실패:', err);
      setLoadError('오류 로그를 불러오지 못했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, sourceFilter, keyword, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // 필터 상태를 URL 쿼리 파라미터에 동기화 (새로고침 시 유지)
  useEffect(() => {
    const params = new URLSearchParams();
    const today = getToday();
    if (fromDate !== today) params.set('from', fromDate);
    if (toDate !== today) params.set('to', toDate);
    if (sourceFilter) params.set('source', sourceFilter);
    if (keyword.trim()) params.set('keyword', keyword.trim());
    setSearchParams(params, { replace: true });
  }, [fromDate, toDate, sourceFilter, keyword, setSearchParams]);

  const handleReset = () => {
    const today = getToday();
    setFromDate(today);
    setToDate(today);
    setKeyword('');
    setSourceFilter('');
    setPage(1);
    setActionError('');
    setActionMessage('');
  };

  const handleClearAll = async () => {
    if (!confirm('모든 오류 로그를 삭제할까요? 이 작업은 되돌릴 수 없습니다.')) return;
    setClearingAll(true);
    setActionError('');
    setActionMessage('');
    try {
      const { error, count } = await supabase.from('error_logs').delete({ count: 'exact' }).neq('id', 0);
      if (error) throw error;
      setActionMessage(`오류 로그 ${count ?? 0}건이 삭제되었습니다.`);
      setPage(1);
      await fetchLogs();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[ErrorLogManager] 전체 삭제 실패:', err);
      setActionError('전체 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setClearingAll(false);
    }
  };

  const handleDeleteOne = async (id: number) => {
    setDeletingId(id);
    setActionError('');
    setActionMessage('');
    try {
      const { data, error } = await supabase.from('error_logs').delete().eq('id', id).select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        setActionError('해당 로그를 찾을 수 없습니다.');
        return;
      }
      setLogs((prev) => prev.filter((l) => l.id !== id));
      if (selectedLog?.id === id) setSelectedLog(null);
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[ErrorLogManager] 로그 삭제 실패:', err);
      setActionError('삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearOld = async () => {
    if (!confirm('90일이 지난 오류 로그를 모두 삭제할까요? 이 작업은 되돌릴 수 없습니다.')) return;
    setClearingOld(true);
    setActionError('');
    setActionMessage('');
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-error-logs', {});
      if (error) throw error;
      if (!data || data.success !== true) {
        throw new Error(data?.error || 'cleanup failed');
      }
      setActionMessage(`90일 이상 로그 ${data.deleted ?? 0}건이 삭제되었습니다.`);
      setPage(1);
      await fetchLogs();
    } catch (err) {
      if (import.meta.env.DEV) console.error('[ErrorLogManager] 정리 실패:', err);
      setActionError('정리 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setClearingOld(false);
    }
  };

  const datePresets = [
    { label: '오늘', from: getToday(), to: getToday() },
    { label: '어제', from: getYesterday(), to: getYesterday() },
    { label: '최근 3일', from: (() => { const d = new Date(); d.setDate(d.getDate() - 2); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), to: getToday() },
    { label: '최근 7일', from: (() => { const d = new Date(); d.setDate(d.getDate() - 6); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), to: getToday() },
  ];

  const uniqueSources = [...new Set(logs.map((l) => l.source).filter(Boolean))] as string[];

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
        <h4 className="font-heading text-sm text-foreground-700 mb-4 flex items-center gap-2">
          <i className="ri-filter-3-line text-base"></i>
          검색 필터
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">빠른 날짜 선택</label>
            <div className="flex flex-wrap gap-1.5">
              {datePresets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => { setFromDate(preset.from); setToDate(preset.to); }}
                  className={`px-2.5 py-1 rounded-lg text-xs border cursor-pointer whitespace-nowrap transition-colors ${
                    fromDate === preset.from && toDate === preset.to
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-background-300 text-foreground-500 hover:bg-background-100'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">시작 날짜</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">종료 날짜</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">메뉴(소스)</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">전체</option>
              {uniqueSources.map((src) => (
                <option key={src} value={src}>{SOURCE_LABELS[src] || src}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="오류 메시지, 내용 키워드 검색..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg border border-background-300 text-xs text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1"></i> 초기화
            </button>
            <button
              onClick={handleClearOld}
              disabled={clearingOld}
              className="px-4 py-2 rounded-lg border border-secondary-300 text-xs text-secondary-600 hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-timer-line mr-1"></i> {clearingOld ? '정리 중...' : '90일 이상 정리'}
            </button>
            <button
              onClick={handleClearAll}
              disabled={clearingAll}
              className="px-4 py-2 rounded-lg border border-accent-300 text-xs text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-delete-bin-line mr-1"></i> {clearingAll ? '삭제 중...' : '전체 삭제'}
            </button>
          </div>
        </div>
      </div>

      {/* Log Summary */}
      {actionError && (
        <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
          <p className="text-accent-700 text-sm">{actionError}</p>
        </div>
      )}
      {actionMessage && (
        <div className="p-3 rounded-lg bg-secondary-50 border border-secondary-200 flex items-start gap-2">
          <i className="ri-check-line text-secondary-600 text-sm mt-0.5"></i>
          <p className="text-secondary-700 text-sm">{actionMessage}</p>
        </div>
      )}
      <div className="flex items-center gap-3 md:gap-4 flex-wrap">
        <h4 className="font-heading text-sm text-foreground-700 flex items-center gap-2">
          <i className="ri-error-warning-line text-base text-accent-500"></i>
          오류 로그 목록
        </h4>
        <span className="text-xs text-foreground-400">
          총 <strong className="text-foreground-800">{totalCount.toLocaleString()}</strong>건
        </span>
        {loading && (
          <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></span>
        )}
      </div>

      {/* Log content */}
      {loadError ? (
        <div className="bg-background-50 rounded-2xl border border-background-200 py-16 text-center">
          <p className="text-accent-600 text-sm font-medium mb-3">{loadError}</p>
          <button
            onClick={fetchLogs}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i> 다시 시도
          </button>
        </div>
      ) : logs.length === 0 && !loading ? (
        <div className="bg-background-50 rounded-2xl border border-background-200 py-16 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-50 flex items-center justify-center">
            <i className="ri-check-double-line text-2xl text-secondary-500"></i>
          </div>
          <p className="text-foreground-600 text-sm font-medium">오류 로그가 없습니다</p>
          <p className="text-foreground-400 text-xs mt-1">
            현재 필터 조건에 해당하는 오류가 없거나, 모든 작업이 정상 처리되었습니다.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-background-50 rounded-2xl border border-background-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium w-12">#</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">발생 일시</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">오류 유형</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">메뉴</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium">오류 메시지</th>
                  <th className="text-left py-3 px-4 text-foreground-400 font-medium w-28">관리</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-b border-background-100 hover:bg-background-50 transition-colors"
                  >
                    <td className="py-2.5 px-4 text-foreground-400 text-xs font-mono">{log.id}</td>
                    <td className="py-2.5 px-4 text-foreground-500 text-xs whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        log.error_type.includes('excel')
                          ? 'bg-secondary-100 text-secondary-700'
                          : log.error_type.includes('api')
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-accent-100 text-accent-700'
                      }`}>
                        <i className={`${ERROR_TYPE_ICONS[log.error_type] || 'ri-question-line'} text-xs`}></i>
                        {ERROR_TYPE_LABELS[log.error_type] || log.error_type}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className="text-xs text-foreground-500">
                        {SOURCE_LABELS[log.source || ''] || log.source || '-'}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-start gap-2">
                        <p className="text-xs text-foreground-700 line-clamp-2 max-w-xs">
                          {log.error_message || '-'}
                        </p>
                        {log.error_detail && (
                          <button
                            onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                            className="text-foreground-400 hover:text-foreground-600 shrink-0 cursor-pointer mt-0.5"
                            title="상세 로그 보기"
                          >
                            <i className={`text-xs ${expandedId === log.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                          </button>
                        )}
                      </div>
                      {expandedId === log.id && (
                        <div className="mt-2 p-3 rounded-lg bg-background-50 border border-background-200">
                          <pre className="text-xs text-foreground-600 font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                            {formatDetail(log.error_detail)}
                          </pre>
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="px-2.5 py-1 rounded-lg text-xs border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-list-3-line mr-1"></i>원문
                        </button>
                        <button
                          onClick={() => handleDeleteOne(log.id)}
                          disabled={deletingId === log.id}
                          className="px-2 py-1 rounded-lg text-xs text-accent-500 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          <i className={deletingId === log.id ? 'ri-loader-4-line animate-spin' : 'ri-close-line'}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="bg-background-50 rounded-xl border border-background-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-foreground-400">#{log.id}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    log.error_type.includes('excel')
                      ? 'bg-secondary-100 text-secondary-700'
                      : log.error_type.includes('api')
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}>
                    <i className={`${ERROR_TYPE_ICONS[log.error_type] || 'ri-question-line'} text-xs`}></i>
                    {ERROR_TYPE_LABELS[log.error_type] || log.error_type}
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mb-1">{formatDate(log.created_at)}</p>
                <p className="text-xs text-foreground-600 mb-1">
                  메뉴: {SOURCE_LABELS[log.source || ''] || log.source || '-'}
                </p>
                <p className="text-xs text-foreground-700 mb-3 line-clamp-2">
                  {log.error_message || '-'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedLog(log)}
                    className="px-2.5 py-1 rounded-lg text-xs border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    원문 보기
                  </button>
                  <button
                    onClick={() => handleDeleteOne(log.id)}
                    disabled={deletingId === log.id}
                    className="px-2.5 py-1 rounded-lg text-xs text-accent-500 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {deletingId === log.id ? '삭제 중...' : '삭제'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* Detail Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedLog(null); }}
        >
      <div className="bg-background-50 rounded-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col shadow-lg">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-background-200">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  selectedLog.error_type.includes('excel')
                    ? 'bg-secondary-100'
                    : selectedLog.error_type.includes('api')
                    ? 'bg-primary-100'
                    : 'bg-accent-100'
                }`}>
                  <i className={`${ERROR_TYPE_ICONS[selectedLog.error_type] || 'ri-question-line'} text-lg ${
                    selectedLog.error_type.includes('excel')
                      ? 'text-secondary-600'
                      : selectedLog.error_type.includes('api')
                      ? 'text-primary-600'
                      : 'text-accent-600'
                  }`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-base text-foreground-800">
                    {ERROR_TYPE_LABELS[selectedLog.error_type] || selectedLog.error_type}
                  </h3>
                  <p className="text-xs text-foreground-400">#{selectedLog.id} · {formatDate(selectedLog.created_at)}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-foreground-400"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
              <div>
                <label className="block text-xs text-foreground-400 mb-1 font-medium">발생 메뉴</label>
                <p className="text-sm text-foreground-700 bg-background-50 rounded-lg px-3 py-2">
                  {SOURCE_LABELS[selectedLog.source || ''] || selectedLog.source || '-'}
                </p>
              </div>
              <div>
                <label className="block text-xs text-foreground-400 mb-1 font-medium">오류 메시지</label>
                <p className="text-sm text-accent-700 bg-accent-50 rounded-lg px-3 py-2 border border-accent-200">
                  {selectedLog.error_message || '-'}
                </p>
              </div>
              <div>
                <label className="block text-xs text-foreground-400 mb-1 font-medium">오류 상세 로그 (원문)</label>
                <div className="bg-background-50 rounded-lg border border-background-200 p-3">
                  <pre className="text-xs text-foreground-600 font-mono whitespace-pre-wrap break-all max-h-96 overflow-y-auto">
                    {formatDetail(selectedLog.error_detail)}
                  </pre>
                </div>
              </div>
              {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                <div>
                  <label className="block text-xs text-foreground-400 mb-1 font-medium">컨텍스트 정보</label>
                  <div className="bg-background-50 rounded-lg border border-background-200 p-3">
                    <pre className="text-xs text-foreground-600 font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                      {JSON.stringify(selectedLog.context, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 md:p-5 border-t border-background-200 flex items-center justify-between">
              <button
                onClick={() => handleDeleteOne(selectedLog.id)}
                disabled={deletingId === selectedLog.id}
                className="px-4 py-2 rounded-lg text-xs text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-delete-bin-line mr-1"></i> {deletingId === selectedLog.id ? '삭제 중...' : '이 로그 삭제'}
              </button>
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-sm text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}